import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {FITHGameConfig} from "../FITHGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineMax, TweenLite} from "gsap";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";

export class FITHAnticipationView extends BaseView {
    private _anticipationEffect: PIXI.spine.Spine;
    private _anticipationEffectCurrentReel: PIXI.Sprite;
    private _bBlingTl: TimelineMax;
    private _bWinAnimation: PIXI.spine.Spine;
    private _anticipationEffectCurrentReelWagons: PIXI.Sprite[] = [];

    public playAnticipation(reelId: number): void {
        this._anticipationEffect.updateTransform();
        this._anticipationEffect.visible = true;
        new SpineTween(this._anticipationEffect, 0, "base", -1);

        this.updateAnticipationCurrentReel(reelId);
        this._anticipationEffectCurrentReel.visible = true;
        this._anticipationEffectCurrentReel.alpha = 0;
        TweenLite.to(this._anticipationEffectCurrentReel, 0.1, {alpha: 1});
    }

    public doBonusBling(sym: StateReelSymbol): void {
        let bBling: PIXI.Sprite;

        const duration: number = 10 / 30;

        bBling = new PIXI.Sprite(GameResources.getTextures("B_bling")[0]);
        bBling.anchor.set(0.5);
        bBling.blendMode = PIXI.BLEND_MODES.SCREEN;

        if (sym.children.length === 2) {
            sym.addChild(bBling);

            this._bBlingTl.to(bBling, duration, {alpha: 1}, 0);
            this._bBlingTl.to(bBling, duration, {alpha: 0}, duration);
        }

        this._bBlingTl.restart();
    }

    public updateAnticipationCurrentReel(reelId: number): void {
        const xPos: number = Helper.getSymbolPositions(reelId, 0, true)[0];

        this._anticipationEffectCurrentReel.position.x = xPos;
    }

    public stopAnticipation(): void {
        this._bBlingTl.pause().kill();

        this._anticipationEffect.visible = false;
        this._anticipationEffect.state.clearTracks();

        TweenLite.to(this._anticipationEffectCurrentReel, 0.1, {
            alpha: 0,
            onComplete: () => this._anticipationEffectCurrentReel.visible = false
        });

        this._bWinAnimation.visible = false;
        this._bWinAnimation.state.clearTracks();
    }

    public playBWinAnimation(scatterCount: number, x: number, y: number): void {
        const name: string = (scatterCount === 3) ? "land_win_l" : "land_win_h";

        if (!this._bWinAnimation.visible) {
            this._bWinAnimation.updateTransform();
            this._bWinAnimation.visible = true;
        }
        this._bWinAnimation.position.set(x, y);
        this._bWinAnimation.state.clearTracks();
        this._bWinAnimation.skeleton.setToSetupPose();
        this._bWinAnimation.skeleton.setSlotsToSetupPose();
        this._bWinAnimation.state.setEmptyAnimation(0, 0);
        this._bWinAnimation.state.setAnimation(0, name, false);
    }

    public playWagonAnticipationCurrentReel(reelId: number): void {
        const xPos: number = Helper.getSymbolPositions(reelId, 0, true)[0],
            item: PIXI.Sprite = this._anticipationEffectCurrentReelWagons[reelId];

        item.position.x = xPos;
        item.visible = true;
        item.alpha = 0;
        TweenLite.to(item, 0.1, {alpha: 1});
    }

    public stopWagonAnticipationCurrentReel(reelId: number): void {
        TweenLite.to(this._anticipationEffectCurrentReelWagons[reelId], 0.1, {
            alpha: 0,
            onComplete: () => this._anticipationEffectCurrentReelWagons[reelId].visible = false
        });
    }

    /**
     * @inheritDoc
     */
    protected initAnimations() {
        super.initAnimations && super.initAnimations();
        StageManager.getLayer(FITHGameConfig.instance.LAYERS.ANTICIPATION.name).addChild(this);

        this.createAnticipationEffectSpine();
        this.createAnticipationEffectCurrentReel();
        this.createBWinAnimation();
        this.createAnticipationEffectCurrentReelWagons();

        this._bBlingTl = new TimelineMax({
            paused: true,
            repeat: -1
        });
    }

    private createAnticipationEffectSpine(): void {
        const centre: number = FITHGameConfig.instance.GAME_HEIGHT / 2,
            yOffset: number = 60;

        this._anticipationEffect = new PIXI.spine.Spine(GameResources.getSpineAsset("Anticipation"));
        this._anticipationEffect.position.set(centre, centre + yOffset);
        this._anticipationEffect.visible = false;
        this.addChild(this._anticipationEffect);
    }

    private createAnticipationEffectCurrentReel(): void {
        this._anticipationEffectCurrentReel = new PIXI.Sprite(GameResources.getTextures("reel_highlight")[0]);
        this._anticipationEffectCurrentReel.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._anticipationEffectCurrentReel.anchor.set(0.5, 0);
        this._anticipationEffectCurrentReel.position.y = FITHGameConfig.instance.REEL_AREA_POS_Y;
        this._anticipationEffectCurrentReel.visible = false;
        this.addChild(this._anticipationEffectCurrentReel);
    }

    private createBWinAnimation(): void {
        this._bWinAnimation = new PIXI.spine.Spine(GameResources.getSpineAsset("bWin"));
        this._bWinAnimation.visible = false;
        StageManager.getLayer(FITHGameConfig.instance.LAYERS.B_WIN.name).addChild(this._bWinAnimation);
    }

    private createAnticipationEffectCurrentReelWagons(): void {
        const numItems: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;
        let item: PIXI.Sprite;
        for (let i: number = 0; i < numItems; i++) {
            item = new PIXI.Sprite(GameResources.getTextures("reel_highlight")[0]);
            item.visible = false;
            item.scale.set(1, 1.5);
            item.blendMode = PIXI.BLEND_MODES.SCREEN;
            item.anchor.set(0.5, 0);
            item.position.y = 230;

            this._anticipationEffectCurrentReelWagons.push(item);
            this.addChild(item);
        }
    }
}